import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { CustomText3D } from "../../_shared/CustomText3D";
import { findSiteBinForLayoutBin } from "../../../components/layoutTableProxy.js";
import {
    flatFootprintRotationBakedInGeometry,
    orientedLayoutFootprintFt,
    flatProductionGroupRotationY,
} from "../../../components/siteLayoutStice.js";
import { siteUsesEditorLayoutAxes } from "../../../components/layoutPreviewView";
import { idsMatch } from "../../../components/siteSetupDefaults.js";
import * as global from "../../../data/global";

const WALL_CENTER_Y = -2;
const WALL_HEIGHT = 1.25;

const FAN_HEIGHT = WALL_HEIGHT;
const FAN_DEPTH = 2.5;
const FAN_WIDTH = 2.5;
const FAN_BORE = 1.6;
const BLADE_COUNT = 3;

const FAN_GREEN = new THREE.Color(0x00ff00);
const FAN_RED = new THREE.Color(0xff0000);
const FAN_YELLOW = new THREE.Color(0xffff00);
const FAN_HOUSING = new THREE.Color(0xc0c0c0);

function finiteOr(n, fallback) {
    const v = Number(n);
    return Number.isFinite(v) ? v : fallback;
}

function isRoundLayoutBin(layoutBin, bin) {
    if (layoutBin?.isRound) return true;
    const type = String(bin?.type ?? layoutBin?.bin?.type ?? "").toLowerCase();
    return type.includes("steel") || type.includes("concrete");
}

export function fansForLayoutBin(site, layoutBin) {
    if (!site || !layoutBin) return [];
    const bin = findSiteBinForLayoutBin(site, layoutBin);
    const fromBin = bin?.fanList;
    if (Array.isArray(fromBin) && fromBin.length > 0) return fromBin;

    const catalogId = bin?.binDataId ?? bin?.binId ?? layoutBin.binId;
    const binName = String(bin?.binName ?? layoutBin.binName ?? "").trim();
    return (site.fans || []).filter((fan) => {
        if (idsMatch(fan.binId, catalogId)) return true;
        const name = String(fan.binName ?? "").trim();
        return !!(name && binName && name === binName);
    });
}

export function fanStatusLabel(fan) {
    const name = String(fan?.fanName ?? "Fan").trim() || "Fan";
    const mode = fan?.isOnAutomatic ? "A" : "M";
    let text = `${name}-${mode}`;
    if (fan?.isOn) text += " ON";
    else text += " OFF";
    if (fan?.isActive === false) text += " Inactive";
    else if (fan?.alarmOn) text += " CON";
    return text;
}

export function fanStatusColor(fan) {
    if (String(fan?.fanIssue ?? "").trim()) return FAN_YELLOW.clone();
    if (fan?.isOn) return FAN_GREEN.clone();
    return FAN_RED.clone();
}

function fanSideAdd(fanSide) {
    const side = String(fanSide ?? "");
    if (side === "South") return 270;
    if (side === "West") return 180;
    if (side === "North") return 90;
    return 0;
}

function applyCatalogNorth(layoutBin, site, insideRotatedGroup) {
    if (insideRotatedGroup) {
        const rot = flatProductionGroupRotationY(layoutBin, site) || 0;
        if (Math.abs(rot) > 0.001) return false;
    }
    if (orientedLayoutFootprintFt(layoutBin, site)) return false;
    if (flatFootprintRotationBakedInGeometry(layoutBin, site)) return false;
    if (
        site?.isLayoutPreview ||
        site?.isLayoutPreviewSite ||
        site?.isLayoutPreview3d ||
        siteUsesEditorLayoutAxes(site) ||
        site?.layoutSavedFromSiteLayoutEditor
    ) {
        return false;
    }
    return true;
}

function roundFanPose(fan, layoutBin, bin) {
    const diameter = finiteOr(layoutBin?.width, finiteOr(bin?.diameter, 40));
    const posDeg = finiteOr(fan.fanPosition, 0);
    return {
        position: [diameter / 2, WALL_CENTER_Y, 0],
        rotationY: THREE.MathUtils.degToRad(posDeg),
    };
}

function flatFanPose(fan, fans, layoutBin, bin, site, insideRotatedGroup) {
    const width = finiteOr(layoutBin?.width, finiteOr(bin?.width, 40));
    const length = finiteOr(layoutBin?.length, finiteOr(bin?.length, 40));
    const degNorth = finiteOr(
        bin?.degreesToNorth ?? layoutBin?.bin?.degreesToNorth,
        0
    );
    const sideAdd = fanSideAdd(fan.fanSide);
    const addShift = applyCatalogNorth(layoutBin, site, insideRotatedGroup)
        ? (degNorth + sideAdd) % 360
        : sideAdd % 360;

    const sameSide = fans.filter(
        (row) => String(row.fanSide ?? "") === String(fan.fanSide ?? "")
    );
    const fansOnThisSide = Math.max(sameSide.length, 1);
    const found = sameSide.findIndex((row) => idsMatch(row.fanId, fan.fanId));
    const index = found >= 0 ? found : 0;
    const fanY = WALL_CENTER_Y;

    let startZ;
    let fanSpacing;
    let fanX;
    let fanZ;
    let fanContainerRotation;

    if (addShift >= 0 && addShift < 90) {
        startZ = -length / 2;
        fanSpacing = length / (fansOnThisSide + 1);
        fanX = width / 2;
        fanZ = -(startZ + fanSpacing * (index + 1));
        fanContainerRotation = 180;
    } else if (addShift >= 90 && addShift < 180) {
        startZ = -width / 2;
        fanSpacing = width / (fansOnThisSide + 1);
        fanX = length / 2;
        fanZ = startZ + fanSpacing * (index + 1);
        fanContainerRotation = 270;
    } else if (addShift >= 180 && addShift < 270) {
        startZ = -length / 2;
        fanSpacing = length / (fansOnThisSide + 1);
        fanX = width / 2;
        fanZ = startZ + fanSpacing * (index + 1);
        fanContainerRotation = 0;
    } else {
        startZ = -width / 2;
        fanSpacing = width / (fansOnThisSide + 1);
        fanX = length / 2;
        fanZ = -(startZ + fanSpacing * (index + 1));
        fanContainerRotation = 90;
    }

    return {
        position: [fanX, fanY, fanZ],
        rotationY: THREE.MathUtils.degToRad(fanContainerRotation),
    };
}

function useFanHousingGeometry() {
    return useMemo(() => {
        const halfW = FAN_WIDTH / 2;
        const shape = new THREE.Shape();
        shape.moveTo(0, -halfW);
        shape.lineTo(FAN_DEPTH, -halfW);
        shape.lineTo(FAN_DEPTH, halfW);
        shape.lineTo(0, halfW);
        shape.closePath();
        const hole = new THREE.Path();
        hole.absarc(FAN_DEPTH / 2, 0, FAN_BORE / 2, 0, Math.PI * 2, true);
        shape.holes.push(hole);
        const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: FAN_HEIGHT,
            bevelEnabled: false,
            curveSegments: 32,
            steps: 1,
        });
        geometry.rotateX(Math.PI / 2);
        geometry.translate(0, FAN_HEIGHT / 2, 0);
        return geometry;
    }, []);
}

function FanBlades({ spinning, color }) {
    const ref = useRef();
    useFrame((_, delta) => {
        if (!ref.current || !spinning) return;
        ref.current.rotation.y += delta * 16;
    });
    const bladeLen = FAN_BORE * 0.43;
    const blades = [];
    for (let i = 0; i < BLADE_COUNT; i += 1) {
        const angle = (i / BLADE_COUNT) * Math.PI * 2;
        blades.push(
            <group key={i} rotation={[0, angle, 0]}>
                <mesh position={[bladeLen / 2 + 0.12, 0, 0]} rotation={[0.35, 0, 0]}>
                    <boxGeometry args={[bladeLen, 0.08, 0.45]} />
                    <meshBasicMaterial color={color} />
                </mesh>
            </group>
        );
    }
    return (
        <group ref={ref} position={[FAN_DEPTH / 2, 0, 0]}>
            <mesh>
                <cylinderGeometry args={[0.18, 0.18, FAN_HEIGHT * 0.7, 12]} />
                <meshBasicMaterial color={FAN_HOUSING} />
            </mesh>
            {blades}
        </group>
    );
}

function fanLiveKey(fan) {
    return `${fan?.isOn ? 1 : 0}|${fan?.isOnAutomatic ? 1 : 0}|${fan?.isActive === false ? 0 : 1}|${fan?.alarmOn ? 1 : 0}|${fan?.fanIssue || ""}`;
}

function resolveLiveFan(fan) {
    const id = fan?.fanId;
    if (id == null) return fan;
    const site = global.gw.selectedSite;
    const lists = [site?.fans, ...(site?.bins || []).map((bin) => bin.fanList)];
    for (const list of lists) {
        if (!Array.isArray(list)) continue;
        const hit = list.find((row) => String(row.fanId) === String(id));
        if (hit) return hit;
    }
    return fan;
}

function FanMesh({ fan, color, spinning }) {
    const housingGeometry = useFanHousingGeometry();
    return (
        <group>
            <mesh geometry={housingGeometry}>
                <meshBasicMaterial
                    color={FAN_HOUSING}
                    transparent
                    opacity={0.9}
                    side={THREE.DoubleSide}
                    depthWrite
                />
            </mesh>
            <FanBlades spinning={!!spinning} color={color} />
            <mesh position={[FAN_DEPTH / 2, FAN_HEIGHT / 2 + 0.22, FAN_WIDTH / 2 + 0.22]}>
                <boxGeometry args={[0.28, 0.28, 0.28]} />
                <meshBasicMaterial color={color} />
            </mesh>
        </group>
    );
}

function fanLabelOffset() {
    return [FAN_DEPTH / 2, FAN_HEIGHT / 2 + 1.15, 0];
}

export function Fan3d({ fan, layoutBin, site, insideRotatedGroup = false }) {
    const bin = findSiteBinForLayoutBin(site, layoutBin);
    const fans = fansForLayoutBin(site, layoutBin);
    const pose = isRoundLayoutBin(layoutBin, bin)
        ? roundFanPose(fan, layoutBin, bin)
        : flatFanPose(fan, fans, layoutBin, bin, site, insideRotatedGroup);
    const liveKeyRef = useRef(fanLiveKey(fan));
    const [, bumpLive] = useState(0);
    useFrame(() => {
        const next = resolveLiveFan(fan);
        const key = fanLiveKey(next);
        if (key === liveKeyRef.current) return;
        liveKeyRef.current = key;
        bumpLive((n) => n + 1);
    });
    const live = resolveLiveFan(fan);
    const color = fanStatusColor(live);
    const label = fanStatusLabel(live);
    const scale = (global.gw.selectedSite?.binTextScale ?? 1) * 0.12;

    return (
        <group rotation={[0, pose.rotationY, 0]}>
            <group position={pose.position}>
                <FanMesh fan={live} color={color} spinning={!!live?.isOn} />
                <CustomText3D
                    scale={scale}
                    side={THREE.DoubleSide}
                    position={fanLabelOffset()}
                    meshProps={{
                        material: new THREE.MeshBasicMaterial({
                            color,
                            side: THREE.FrontSide,
                            toneMapped: false,
                            transparent: false,
                            opacity: 1,
                        }),
                    }}
                >
                    {label}
                </CustomText3D>
            </group>
        </group>
    );
}

export function FanBin3d({ layoutBin, site, insideRotatedGroup = false }) {
    useEffect(() => {
        global.startLiveFanStatusPoll();
        return () => global.stopLiveFanStatusPoll();
    }, []);
    const fans = fansForLayoutBin(site, layoutBin);
    if (!fans.length) return null;
    return fans.map((fan) => (
        <Fan3d
            key={`fan_${fan.fanId ?? fan.fanName}_${fan.fanNumber ?? 0}`}
            fan={fan}
            layoutBin={layoutBin}
            site={site}
            insideRotatedGroup={insideRotatedGroup}
        />
    ));
}

export default FanBin3d;
